import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { ConfirmationService, MessageService } from 'primeng/api';
import { AppService } from '../../../services/app.service';
import { TransactionsService } from '../../../services/transactions.service';
import { UtilsService } from '../../../services/utils.service';
import { AuthService } from '../../../services/auth.service';
import { Roles } from '../../../models/roles';
import { UserService } from '../../../services/users.service';
import { LoggerService } from '../../../services/logger.service';
import { COUNTRY_OPTIONS } from '../../../models/countries';
import { TransfersService } from '../../../services/transfers.service';

@Component({
  selector: 'app-add-internal',
  templateUrl: './add-internal.component.html',
  styleUrl: './add-internal.component.scss',
})
export class AddInternalComponent implements OnInit, OnDestroy {
  @Input() currency: string = '';
  currencyPicker: any = null;

  roles = Roles;
  countryOptions = COUNTRY_OPTIONS;

  transactionTypes: Array<{ currency: string; id: string }> = [];
  transaction: any;
  showTransactionModal = false;

  showNewTransaction = false;
  showNewTransactionType = false;
  newTransactionType: null | string = null;
  addingTransaction = false;
  showInternalConfirmation = false;
  pendingInternalBody: any = null;
  internalConfirmationDetails: Array<{ label: string; value: string }> = [];

  transferTypes = [
    {
      label: 'Simple',
      description:
        'Debes seleccionar un wallet origen con balance disponible desde el cual enviar el dinero.',
      value: 'simple',
    },
    {
      label: 'Multiple',
      description:
        'El sistema determinará de qué wallets enviaremos el dinero dependiendo de tu balance.',
      value: 'multiple',
    },
  ];

  newTransactionForm = this.formBuilder.group({
    amount: ['', Validators.required],
    client_id: [
      !this.auth.hasRole(Roles.Admin) ? this.auth.user.user_id : null,
      Validators.required,
    ],
    destiny_user: ['', Validators.required],
    wallet_id: [''],
    destiny_wallet_id: [''],
    currency: [this.currency, Validators.required],
    transfer_type: [this.transferTypes[1], Validators.required],
    type: ['internal'],
  });

  showCurrency = false;

  wallets: any = [];

  constructor(
    private transfersService: TransfersService,
    private formBuilder: FormBuilder,
    public utilsService: UtilsService,
    private confirmationService: ConfirmationService,
    private messageService: MessageService,
    public auth: AuthService,
    public appService: AppService,
    public userService: UserService,
    private activatedRoute: ActivatedRoute,
    private logger: LoggerService,
  ) {}

  ngOnInit() {
    this.openNewTransaction();
    if (!this.currencyPicker) this.showCurrency = true;
    else {
      this.selectCurrency(this.currencyPicker);
      this.continue();
    }
  }

  ngOnDestroy(): void {}

  selectCurrency(currency: any) {
    const selected = currency?.value ?? currency;
    this.currency = this.extractCurrencyId(selected);

    if (!this.isInternalTransferCurrencyAllowed(this.currency)) {
      this.currency = '';
      this.currencyPicker = null;
      this.wallets = [];
      this.newTransactionForm.controls.currency.setValue(null);
      return;
    }

    this.currencyPicker = selected;

    this.newTransactionForm.controls.currency.setValue(this.currency || null);
    this.wallets = this.getWallets(this.currency);
  }

  resetForm() {
    this.newTransactionForm = this.formBuilder.group({
      amount: ['', Validators.required],
      client_id: [
        !this.auth.hasRole(Roles.Admin) ? this.auth.user.user_id : null,
        Validators.required,
      ],
      destiny_user: ['', Validators.required],
      wallet_id: [''],
      destiny_wallet_id: [''],
      currency: [this.currency, Validators.required],
      transfer_type: [this.transferTypes[1], Validators.required],
      type: ['internal'],
    });
  }

  openNewTransaction = () => {
    if (this.currency && !this.isInternalTransferCurrencyAllowed(this.currency)) {
      this.currency = '';
    }

    this.showNewTransactionType = true;
    this.newTransactionType = null;
    this.newTransactionForm.reset();

    this.resetForm();

    this.currencyPicker = this.allowedInternalTransferCurrencies.find(
      (el: any) => el.id === this.currency,
    );
  };

  exitNewTransaction = () => {
    this.appService.triggerAddInternal(false);
    this.showNewTransactionType = false;
    this.showNewTransaction = false;
    this.showInternalConfirmation = false;
    this.pendingInternalBody = null;
    this.internalConfirmationDetails = [];
  };

  continue() {
    if (this.checkContinueDisabled()) return;

    this.showNewTransactionType = false;
    this.showNewTransaction = true;
  }

  saveTransaction() {
    if (!this.auth.guardTransactionalAction()) return;
    const clientControlValue = this.newTransactionForm.controls.client_id.value;
    const clientId =
      typeof clientControlValue === 'object' && clientControlValue
        ? (clientControlValue as any).user_id
        : clientControlValue;

    const base = {
      amount: this.newTransactionForm.controls.amount.value,
      client_id: clientId,
      destiny_user: this.newTransactionForm.controls.destiny_user.value,
      wallet_id: (this.newTransactionForm.controls.wallet_id.value as any)?.id,
      destiny_wallet_id: (
        this.newTransactionForm.controls.destiny_wallet_id.value as any
      )?.id,
      currency: this.currency,
      transfer_type:
        this.newTransactionForm.controls.transfer_type.value?.value,
      type: 'internal',
    };

    this.openInternalConfirmation(base);
  }

  private openInternalConfirmation(body: any): void {
    this.pendingInternalBody = body;
    this.internalConfirmationDetails =
      this.buildInternalConfirmationDetails(body);
    this.showInternalConfirmation = true;
  }

  backToInternalForm(): void {
    this.showInternalConfirmation = false;
  }

  confirmInternal(): void {
    if (!this.pendingInternalBody || this.addingTransaction) {
      return;
    }

    this.addingTransaction = true;
    this.addTransaction(this.pendingInternalBody);
  }

  private buildInternalConfirmationDetails(
    body: any,
  ): Array<{ label: string; value: string }> {
    const isAdmin = this.auth.hasRole(Roles.Admin);
    const isSimpleTransfer = body?.transfer_type === 'simple';
    const originWallet = this.wallets?.find(
      (wallet: any) => wallet?.id === body?.wallet_id,
    );
    const destinyWallet = this.wallets?.find(
      (wallet: any) => wallet?.id === body?.destiny_wallet_id,
    );

    const details = [
      {
        label: 'Monto',
        value: this.formatConfirmationAmount(body?.amount, body?.currency),
      },
      { label: 'Usuario receptor', value: body?.destiny_user },
    ];

    if (isAdmin) {
      details.splice(1, 0, {
        label: 'Cliente asignado',
        value: this.getClientConfirmationLabel(body),
      });
      details.push({ label: 'Modalidad', value: body?.transfer_type });

      if (isSimpleTransfer) {
        details.push({
          label: 'Wallet origen',
          value: originWallet?.wallet_shortname ?? body?.wallet_id,
        });

        if (body?.destiny_wallet_id) {
          details.push({
            label: 'Wallet destino',
            value: destinyWallet?.wallet_shortname ?? body?.destiny_wallet_id,
          });
        }
      }
    }

    return details
      .filter((detail) => this.hasConfirmationValue(detail.value))
      .map((detail) => ({
        ...detail,
        value: this.stringifyConfirmationValue(detail.value),
      }));
  }

  private getClientConfirmationLabel(body: any): string {
    const clientId = body?.client_id;
    const client = this.userService.users?.find(
      (item: any) => Number(item?.user_id ?? item?.id) === Number(clientId),
    );

    return client?.username ?? client?.email ?? clientId ?? '';
  }

  private formatConfirmationAmount(amount: any, currency: any): string {
    if (!this.hasConfirmationValue(amount)) {
      return '';
    }

    const numeric = Number(amount);
    const formatted = Number.isFinite(numeric)
      ? numeric.toLocaleString('es-VE', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 8,
        })
      : amount.toString();

    return `${currency ?? this.currency} ${formatted}`.trim();
  }

  private hasConfirmationValue(value: any): boolean {
    return value !== null && value !== undefined && value !== '';
  }

  private stringifyConfirmationValue(value: any): string {
    if (typeof value === 'object') {
      return JSON.stringify(value);
    }

    return value.toString();
  }

  addTransaction(body: any) {
    this.transfersService.addTransfer(body).subscribe(
      (resp) => {
        this.logger.log(resp);

        this.messageService.add({
          severity: 'success',
          summary: 'Listo',
          detail: 'Hemos agregado la transferencia correctamente.',
        });
        this.addingTransaction = false;
        this.showInternalConfirmation = false;
        this.pendingInternalBody = null;
        this.showNewTransaction = false;
        this.exitNewTransaction();
      },
      (err) => {
        this.logger.log(err);

        this.addingTransaction = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: ` ${err.message === 'Currency not found' ? 'No tienes saldo suficiente para realizar esta transferencia.' : 'Ocurrió un error al agregar la transferencia.' + err.message}`,
        });
      },
    );
  }

  closeAddModal = () => {
    this.appService.triggerAddInternal(false);
    this.showNewTransactionType = false;
    this.showNewTransaction = false;
    this.showInternalConfirmation = false;
    this.pendingInternalBody = null;
    this.internalConfirmationDetails = [];
  };

  checkContinueDisabled = () => {
    if (!this.currencyPicker) {
      return true;
    }

    if (!this.isInternalTransferCurrencyAllowed(this.currency)) {
      return true;
    }

    return false;
  };

  get allowedInternalTransferCurrencies(): any[] {
    return this.userService.getPlatformServiceAllowedCurrencies(
      'internal_transfer',
    );
  }

  private extractCurrencyId(option: any): string {
    if (!option) {
      return '';
    }

    if (typeof option === 'string') {
      return option.trim().toUpperCase();
    }

    if (typeof option === 'object') {
      const { id, currency, code } = option as {
        id?: string;
        currency?: string;
        code?: string;
      };

      const raw = id ?? currency ?? code;
      if (raw) {
        return raw.toString().trim().toUpperCase();
      }
    }

    return option.toString().trim().toUpperCase();
  }

  private isInternalTransferCurrencyAllowed(currency: any): boolean {
    return this.userService.isPlatformServiceCurrencyAllowed(
      'internal_transfer',
      currency,
    );
  }

  getDescription() {
    return (this.newTransactionForm.controls.transfer_type.value as any)
      .description;
  }

  getWallets(country: string) {
    let wallets = this.userService.user_balance_config.currencies.find(
      (el: any) => el.id === country,
    )?.subwallets;

    if (this.auth.hasRole(Roles.Admin)) {
      wallets.forEach((element: any) => {
        element.wallet_shortname = element.shortname;
      });
      return wallets;
    } else {
      let new_wallets = wallets.filter((el: any) => Number(el.balance) > 0);

      new_wallets.forEach((element: any, index: number) => {
        element.wallet_shortname =
          'Wallet ' + (index + 1) + ' - Balance: ' + Number(element.balance);
      });
      return new_wallets;
    }
  }

  private isSimpleTransfer(): boolean {
    return (
      this.newTransactionForm.controls.transfer_type.value?.value === 'simple'
    );
  }

  private getAmount(): number {
    return Number(this.newTransactionForm.controls.amount.value || 0);
  }

  private getSelectedWalletBalance(): number {
    return Number(
      (this.newTransactionForm.controls.wallet_id.value as any)?.balance || 0,
    );
  }

  private getAvailableBalance(): number {
    return Number(this.currencyPicker.balance.total || 0);
  }

  validSelector(): boolean {
    const amount = this.newTransactionForm.controls.amount.value;
    const wallet = this.newTransactionForm.controls.wallet_id.value;

    if (!amount) return false;
    if (this.isSimpleTransfer() && (!wallet || wallet === '')) return false;

    return true;
  }

  insufficientFunds(): boolean {
    if (this.auth.hasRole(Roles.Admin)) return false;
    if (!this.validSelector()) return true;

    const amount = this.getAmount();
    const balance = this.isSimpleTransfer()
      ? this.getSelectedWalletBalance()
      : this.getAvailableBalance();

    return amount > balance;
  }
}
